/* global MessageEvent */
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Form, toast } from '@qodo/design-system';
import axios from 'axios';
import { AlertTriangle, UserCog } from 'lucide-react';
import { BitbucketWorkspaceInfo } from 'network/httpClients/RepositoryClient/api.schema';
import { useBitbucketCreateInstallationMutation } from 'network/mutations/useBitbucketCreateInstallationMutation';
import { useBitbucketOAuthSessionCreateMutation } from 'network/mutations/useBitbucketOAuthSessionCreateMutation';
import { useBitbucketOAuthSessionQuery } from 'network/queries/useBitbucketOAuthSessionQuery';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { spawn } from 'utils/translations';
import { useBitbucketInstallation } from '../bitbucketInstallationContext';
import { FORM_IDS } from '../consts';
import { BITBUCKET_OAUTH_MESSAGE_TYPE } from '../parts/OAuthCallbackReceiver';

const spawnGuide = spawn('dialogs.bitbucket-installation.install-guide');
const spawnErrors = spawn('dialogs.bitbucket-installation.errors');

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

const openOAuthPopup = (url: string): ReturnType<typeof window.open> => {
  const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
  const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;
  const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},popup=yes`;

  return window.open(url, 'bitbucket-oauth', features);
};

const buildPopupUrl = ({
  oauthPopupBaseUrl,
  oauthApiBaseUrl,
  oauthClientId,
  oauthScopes,
  callbackUrl,
}: {
  oauthPopupBaseUrl: string;
  oauthApiBaseUrl: string;
  oauthClientId: string;
  oauthScopes?: string;
  callbackUrl?: string;
}): string => {
  const params = new URLSearchParams({
    origin: window.location.origin,
    client_id: oauthClientId,
    api_base_url: oauthApiBaseUrl,
    ...(oauthScopes && { scope: oauthScopes }),
    ...(callbackUrl && { callback_url: callbackUrl }),
  });

  return `${oauthPopupBaseUrl}/integrations/bitbucket/oauth?${params.toString()}`;
};

export const InstallGuide = () => {
  const { setIsNextEnabled, setIsPending, goNext } = useMultiStepDialog();
  const {
    oauthPopupBaseUrl,
    oauthApiBaseUrl,
    oauthClientId,
    oauthScopes,
    callbackUrl,
    sessionId,
    setSessionId,
    setSelectedWorkspace,
    setBitbucketWorkspaceSlug,
  } = useBitbucketInstallation();

  const [pendingSessionId, setPendingSessionId] = useState<
    string | undefined
  >();
  const [multipleWorkspaces, setMultipleWorkspaces] = useState<
    BitbucketWorkspaceInfo[]
  >([]);
  const [oauthErrorDetail, setOauthErrorDetail] = useState<
    string | undefined
  >();

  const popupRef = useRef<ReturnType<typeof window.open>>(undefined);

  const methods = useForm();
  const { mutate: createSession } = useBitbucketOAuthSessionCreateMutation();
  const { mutate: createInstallation } =
    useBitbucketCreateInstallationMutation();

  const { data: sessionData, error: sessionError } =
    useBitbucketOAuthSessionQuery(pendingSessionId);

  useEffect(() => {
    if (!sessionError) return;

    const rawMessage = axios.isAxiosError(sessionError)
      ? sessionError.response?.data?.error?.message
      : undefined;
    const backendMessage = rawMessage?.replace(/^\d{3}:\s*/, '');

    setOauthErrorDetail(backendMessage || spawnErrors('oauth-session-invalid'));
    setIsPending(false);
    setIsNextEnabled(true);
  }, [sessionError]);

  useEffect(() => {
    if (!sessionData) return;

    const { workspaces } = sessionData;

    if (workspaces.length === 0) {
      toast.error(spawnErrors('oauth-no-workspaces'));
      setIsPending(false);
      setIsNextEnabled(true);

      return;
    }

    if (workspaces.length === 1) {
      startInstallationForWorkspace(workspaces[0]);
    } else {
      setMultipleWorkspaces(workspaces);
      setIsPending(false);
      setIsNextEnabled(false);
    }
  }, [sessionData]);

  useEffect(() => {
    if (!oauthPopupBaseUrl) return;

    const expectedOrigin = new URL(oauthPopupBaseUrl).origin;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return;

      if (event.data?.type !== BITBUCKET_OAUTH_MESSAGE_TYPE) return;

      const { token, error, errorDetail } = event.data;

      if (error) {
        if (errorDetail) {
          setOauthErrorDetail(errorDetail);
        } else {
          toast.error(spawnErrors('oauth-start-failed'));
        }

        setIsPending(false);
        setIsNextEnabled(true);

        return;
      }

      if (token) {
        createSession(
          { token },
          {
            onSuccess: (data) => {
              setSessionId(data.sessionId);
              setPendingSessionId(data.sessionId);
            },
            onError: () => {
              toast.error(spawnErrors('oauth-start-failed'));
              setIsPending(false);
              setIsNextEnabled(true);
            },
          },
        );

        return;
      }

      toast.error(spawnErrors('oauth-start-failed'));
      setIsPending(false);
      setIsNextEnabled(true);
    };

    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
  }, [oauthPopupBaseUrl]);

  const startInstallationForWorkspace = (workspace: BitbucketWorkspaceInfo) => {
    setSelectedWorkspace(workspace);
    setMultipleWorkspaces([]);

    const sid = sessionId ?? pendingSessionId;

    if (!sid) return;

    createInstallation(
      {
        workspaceSlug: workspace.slug,
        workspaceName: workspace.name,
        sessionId: sid,
      },
      {
        onSuccess: (data) => {
          setBitbucketWorkspaceSlug(data.bitbucketWorkspaceSlug);
          setIsPending(false);
          goNext();
        },
        onError: () => {
          toast.error(spawnErrors('create-installation-failed'));
          setIsPending(false);
          setIsNextEnabled(true);
        },
      },
    );
  };

  const onInstallApp = () => {
    if (!oauthPopupBaseUrl || !oauthClientId) return;

    setOauthErrorDetail(undefined);
    setIsPending(true);
    setIsNextEnabled(false);

    const popupUrl = buildPopupUrl({
      oauthPopupBaseUrl,
      oauthApiBaseUrl,
      oauthClientId,
      oauthScopes,
      callbackUrl,
    });

    popupRef.current = openOAuthPopup(popupUrl);
  };

  return (
    <Step title={spawnGuide('title')} description={spawnGuide('description')}>
      <Form
        {...({ id: FORM_IDS.GRANT_ACCESS } as Record<string, string>)}
        methods={methods}
        onSubmit={onInstallApp}
      >
        <div className="flex flex-col gap-6">
          {oauthErrorDetail && (
            <div className="flex items-start gap-2 rounded-md border border-red-700 bg-red-950 p-3 text-sm text-red-300">
              <AlertTriangle className="size-4 shrink-0 mt-0.5 text-red-400" />
              <span>{oauthErrorDetail}</span>
            </div>
          )}

          <div className="flex items-center gap-4 w-full">
            <UserCog className="size-4 shrink-0 text-text-general-neutral-mid" />
            <p className="text-h5 text-text-general-neutral-mid">
              {spawnGuide('warning-admin')}
            </p>
          </div>

          <div className="flex items-center gap-4 w-full">
            <AlertTriangle className="size-4 shrink-0 text-text-general-neutral-mid" />
            <p className="text-h5 text-text-general-neutral-mid">
              {spawnGuide('warning-logged-in')}
            </p>
          </div>

          {multipleWorkspaces.length > 0 && (
            <div className="flex flex-col gap-2">
              {multipleWorkspaces.map((workspace) => (
                <button
                  key={workspace.slug}
                  type="button"
                  className="flex flex-col items-start gap-0.5 rounded-lg border border-border-bg-neutral px-4 py-3 text-left hover:bg-fill-panel-neutral"
                  onClick={() => {
                    setIsPending(true);
                    startInstallationForWorkspace(workspace);
                  }}
                >
                  <span className="font-medium text-sm">{workspace.name}</span>
                  <span className="text-xs text-text-bg-neutral-mid">
                    {workspace.slug}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </Form>
    </Step>
  );
};
